// https://github.com/masterWeber/highlight-in-textarea
//modified to work with ES6 modules, and use an array of word indices to highlight

const HighlightInTextarea = function(el, config) {
  this.init(el, config);
};

HighlightInTextarea.instance = function(el, config) {
  return new HighlightInTextarea(el, config);
};

HighlightInTextarea.prototype = {
  ID: 'hit',

  init: function (el, config) {

    if (typeof el === 'string') {
      this.el = document.querySelector(el);
    } else {
      this.el = el;
    }

    if (this.getType(config) === 'custom') {
      this.highlightArrays = config.highlightArrays || [];
      this.generate();
    } else {
      console.error('valid config object not provided');
    }
  },

  // returns identifier strings that aren't necessarily "real" JavaScript types
  getType: function (instance) {
    let type = typeof instance;
    if (!instance) {
      return 'falsey';
    } else if (Array.isArray(instance)) {
      if (instance.length === 2 && typeof instance[0] === 'number' &&
        typeof instance[1] === 'number') {
        return 'range';
      } else {
        return 'array';
      }
    } else if (type === 'object') {
      if (instance instanceof RegExp) {
        return 'regexp';
      } else if (instance.hasOwnProperty('highlightArrays')) {
        return 'custom';
      } else if (instance.hasOwnProperty('highlight')) {
        return 'custom';
      }
    } else if (type === 'function' || type === 'string') {
      return type;
    }

    return 'other';
  },

  generate: function () {
    if (!this.container) {
      this.container = this.el.closest('.' + this.ID + '-container');
      if (!this.container) {
        console.error('missing container for highlight in textarea');
        return;
      }
      this.backdrop = this.container.querySelector('.' + this.ID + '-backdrop');
      this.highlights = this.container.querySelector('.' + this.ID + '-highlights');

      if (!this.backdrop || !this.highlights) {
        console.error('missing required elements for highlight in textarea');
        return;
      }

      this.el.addEventListener('scroll', this.handleScroll.bind(this));
      this.container.addEventListener('scroll', this.blockContainerScroll.bind(this));
    }

    // Just update the highlights content
    this.handleInput();
  },

  handleInput: function () {
    let input = this.el.value;
    let ranges = this.getRangesFromArrays(input, this.highlightArrays);
    let unstaggeredRanges = this.removeStaggeredRanges(ranges);
    let boundaries = this.getBoundaries(unstaggeredRanges);
    this.renderMarks(boundaries);
  },

  getRangesFromArrays: function (input, highlightArrays) {
    if (!highlightArrays || highlightArrays.length === 0) {
      return [];
    }

    const words = input.split(/\s+/).filter(word => word.length > 0);
    let ranges = [];
    let currentIndex = 0;
    let wordPositions = [];

    // First pass: find all word positions
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      const wordMatch = input.substring(currentIndex).match(/\S+/);
      if (!wordMatch) break;

      const wordStart = currentIndex + input.substring(currentIndex).indexOf(wordMatch[0]);
      const wordEnd = wordStart + wordMatch[0].length;

      wordPositions.push({ start: wordStart, end: wordEnd, index: wordIndex });

      // Move past this word and any following whitespace
      currentIndex = wordEnd;
      while (currentIndex < input.length && /\s/.test(input[currentIndex])) {
        currentIndex++;
      }
    }

    // Track which word positions are already highlighted (for priority)
    const highlightedPositions = new Set();

    // Second pass: create continuous ranges for each highlight array (in priority order)
    for (let arrayIndex = 0; arrayIndex < highlightArrays.length; arrayIndex++) {
      const highlightArray = highlightArrays[arrayIndex];
      if (!highlightArray) continue;

      let rangeStart = null;
      let lastHighlightedIndex = null;

      for (let wordIndex = 0; wordIndex <= words.length; wordIndex++) {
        const shouldHighlight = wordIndex < words.length &&
                               highlightArray[wordIndex] === true &&
                               !highlightedPositions.has(wordIndex); // Skip if already highlighted by higher priority array

        if (shouldHighlight) {
          if (rangeStart === null) {
            // Start of a new range
            rangeStart = wordPositions[wordIndex].start;
          }
          lastHighlightedIndex = wordIndex;
          // Mark this position as highlighted
          highlightedPositions.add(wordIndex);
        } else if (rangeStart !== null) {
          // End of current range - create range from start to end of last highlighted word
          const rangeEnd = wordPositions[lastHighlightedIndex].end;
          const range = [rangeStart, rangeEnd];
          range.className = `highlight-${arrayIndex}`;
          ranges.push(range);
          rangeStart = null;
          lastHighlightedIndex = null;
        }
      }

      // Handle case where range extends to end of text
      if (rangeStart !== null && lastHighlightedIndex !== null) {
        const rangeEnd = wordPositions[lastHighlightedIndex].end;
        const range = [rangeStart, rangeEnd];
        range.className = `highlight-${arrayIndex}`;
        ranges.push(range);
      }
    }

    return ranges;
  },

  getRanges: function (input, highlight) {
    // Keep existing method for backward compatibility, but prioritize array-based highlighting
    if (this.highlightArrays && this.highlightArrays.length > 0) {
      return this.getRangesFromArrays(input, this.highlightArrays);
    }

    let type = this.getType(highlight);
    switch (type) {
      case 'array':
        return this.getArrayRanges(input, highlight);
      case 'function':
        return this.getFunctionRanges(input, highlight);
      case 'regexp':
        return this.getRegExpRanges(input, highlight);
      case 'string':
        return this.getStringRanges(input, highlight);
      case 'range':
        return this.getRangeRanges(input, highlight);
      case 'custom':
        return this.getCustomRanges(input, highlight);
      default:
        if (!highlight) {
          // do nothing for falsey values
          return [];
        } else {
          console.error('unrecognized highlight type');
        }
    }
  },

  getArrayRanges: function (input, arr) {
    let ranges = arr.map(this.getRanges.bind(this, input));
    return Array.prototype.concat.apply([], ranges);
  },

  getFunctionRanges: function (input, func) {
    return this.getRanges(input, func(input));
  },

  getRegExpRanges: function (input, regex) {
    let ranges = [];
    let match;
    while (match = regex.exec(input), match !== null) {
      ranges.push([match.index, match.index + match[0].length]);
      if (!regex.global) {
        // non-global regexes do not increase lastIndex, causing an infinite loop,
        // but we can just break manually after the first match
        break;
      }
    }
    return ranges;
  },

  getStringRanges: function (input, str) {
    let ranges = [];
    let inputLower = input.toLowerCase();
    let strLower = str.toLowerCase();
    let index = 0;
    while (index = inputLower.indexOf(strLower, index), index !== -1) {
      ranges.push([index, index + strLower.length]);
      index += strLower.length;
    }
    return ranges;
  },

  getRangeRanges: function (input, range) {
    return [range];
  },

  getCustomRanges: function (input, custom) {
    let ranges = this.getRanges(input, custom.highlight);
    if (custom.className) {
      ranges.forEach(function (range) {
        // persist class name as a property of the array
        if (range.className) {
          range.className = custom.className + ' ' + range.className;
        } else {
          range.className = custom.className;
        }
      });
    }
    return ranges;
  },

  // prevent staggered overlaps (clean nesting is fine)
  removeStaggeredRanges: function (ranges) {
    let unstaggeredRanges = [];
    ranges.forEach(function (range) {
      let isStaggered = unstaggeredRanges.some(function (unstaggeredRange) {
        let isStartInside = range[0] > unstaggeredRange[0] && range[0] <
          unstaggeredRange[1];
        let isStopInside = range[1] > unstaggeredRange[0] && range[1] <
          unstaggeredRange[1];
        return isStartInside !== isStopInside; // xor
      });
      if (!isStaggered) {
        unstaggeredRanges.push(range);
      }
    });
    return unstaggeredRanges;
  },

  getBoundaries: function (ranges) {
    let boundaries = [];
    ranges.forEach(function (range) {
      boundaries.push({
        type: 'start',
        index: range[0],
        className: range.className,
      });
      boundaries.push({
        type: 'stop',
        index: range[1],
      });
    });

    this.sortBoundaries(boundaries);
    return boundaries;
  },

  sortBoundaries: function (boundaries) {
    // backwards sort (since marks are inserted right to left)
    boundaries.sort(function (a, b) {
      if (a.index !== b.index) {
        return b.index - a.index;
      } else if (a.type === 'stop' && b.type === 'start') {
        return 1;
      } else if (a.type === 'start' && b.type === 'stop') {
        return -1;
      } else {
        return 0;
      }
    });
  },

  renderMarks: function (boundaries) {
    let input = this.el.value;
    boundaries.forEach(function (boundary, index) {
      let markup;
      if (boundary.type === 'start') {
        markup = '{{hit-mark-start|' + index + '}}';
      } else {
        markup = '{{hit-mark-stop}}';
      }
      input = input.slice(0, boundary.index) + markup +
        input.slice(boundary.index);
    });

    // this keeps scrolling aligned when input ends with a newline
    input = input.replace(/\n({{hit-mark-stop}})?$/, '\n\n$1');

    // encode HTML entities
    input = input.replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // replace start tokens with opening <mark> tags with class name
    input = input.replace(/{{hit-mark-start\|(\d+)}}/g,
      function (match, subMatch) {
        const className = boundaries[+subMatch].className;
        if (className) {
          return '<mark class="' + className + '">';
        } else {
          return '<mark>';
        }
      });

    // replace stop tokens with closing </mark> tags
    input = input.replace(/{{hit-mark-stop}}/g, '</mark>');

    this.highlights.innerHTML = input;
  },

  handleScroll: function () {
    this.backdrop.scrollTop = this.el.scrollTop;

    // Chrome and Safari won't break long strings of spaces, which can cause
    // horizontal scrolling, this compensates by shifting highlights by the
    // horizontally scrolled amount to keep things aligned
    let scrollLeft = this.el.scrollLeft;

    if (scrollLeft > 0) {
      this.backdrop.style.transform = 'translateX(' + -scrollLeft + 'px)';
    } else {
      this.backdrop.style.transform = '';
    }
  },

  // in Chrome, page up/down in the textarea will shift stuff within the
  // container (despite the CSS), this immediately reverts the shift
  blockContainerScroll: function () {
    this.container.scrollLeft = 0;
  },
}

// Export for ES6 modules
export { HighlightInTextarea };
